import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { PasseLivreService } from 'src/app/core/services/passe-livre/passe-livre.service';
import { StatusTicketCard } from 'src/app/flight/models/StatusTicketCard';
import { NotificationService } from 'src/app/shared/services/notification.service';
import { RequestStatus } from 'src/app/models/RequestStatus';
import { CREW_INFO, getItemFromSessionStorage } from 'src/app/utils/SessionStorageUtil';
import { CrewInfo } from 'src/app/models/CrewInfo';
import { TicketResponse, TicketsResponse } from '../models/Ticket';
import { forkJoin, of } from 'rxjs';
import { catchError, timeout } from 'rxjs/operators';
import { KeyValue } from '@angular/common';
import { SeatCountFlight, SeatCountResponse } from '../models/SeatCountResponse';
import { FlightCrewSummaryResponse } from '../models/FlightCrewItem';

@Component({
    selector: 'app-my-flights',
    templateUrl: './my-flights.component.html',
    styleUrls: ['./my-flights.component.scss'],
    standalone: false
})
export class MyFlightsComponent implements OnInit {
  StatusTicketCard = StatusTicketCard;

  cards = new Map<StatusTicketCard, TicketResponse[]>([]);

  ticketsRequestStatus: RequestStatus = new RequestStatus();

  static instance: MyFlightsComponent;

  ticketMap: Map<string, TicketResponse[]> = new Map();

  ticketMapActive: Map<string, TicketResponse[]> = new Map();
  ticketMapInactive: Map<string, TicketResponse[]> = new Map();
  ticketMapCancelled: Map<string, TicketResponse[]> = new Map();

  /** Segmented control Ativos / Cancelados / Histórico. Purely presentational: it only picks which map is rendered. */
  readonly statusTabs: ReadonlyArray<{ status: StatusTicketCard; label: string }> = [
    { status: StatusTicketCard.ACTIVE, label: 'Ativos' },
    { status: StatusTicketCard.CANCELLED, label: 'Cancelados' },
    { status: StatusTicketCard.INACTIVE, label: 'Histórico' }
  ];

  selectedStatus: StatusTicketCard = StatusTicketCard.ACTIVE;

  selectStatus(status: StatusTicketCard): void {
    this.selectedStatus = status;
  }

  /** Map of the selected category, grouped by PNR (same maps filled by mountCards). */
  get visibleTickets(): Map<string, TicketResponse[]> {
    return this.ticketMapFor(this.selectedStatus);
  }

  /** Number of cards rendered for a category: 1 card per PNR, except 4 tickets which render as 2 cards. */
  cardCount(status: StatusTicketCard): number {
    let count = 0;
    this.ticketMapFor(status).forEach(tickets => {
      count += tickets.length === 4 ? 2 : (tickets.length >= 1 && tickets.length <= 3 ? 1 : 0);
    });
    return count;
  }

  private ticketMapFor(status: StatusTicketCard): Map<string, TicketResponse[]> {
    switch (status) {
      case StatusTicketCard.CANCELLED: return this.ticketMapCancelled;
      case StatusTicketCard.INACTIVE: return this.ticketMapInactive;
      default: return this.ticketMapActive;
    }
  }

  constructor(private passeLivreService: PasseLivreService,
              private notificationService: NotificationService,
              private changeDetector: ChangeDetectorRef) {
                MyFlightsComponent.instance = this;
              }

  ngOnInit(): void {
    this.getTickets();
  }

  public refreshTickets() {
    const crewInfo = getItemFromSessionStorage<CrewInfo>(CREW_INFO);
    if (crewInfo) {
      this.getTickets();
    }
  }

  public getTickets(): void {
    const fallback = of({ tickets: [] } as TicketsResponse);
    const wrap = (obs: ReturnType<typeof this.passeLivreService.getTickets>) =>
      obs.pipe(timeout(15000), catchError(() => fallback));

    const active$   = wrap(this.passeLivreService.getTickets('ACTIVE'));
    const cancelled$ = wrap(this.passeLivreService.getTickets('CANCELLED'));
    const inactive$ = wrap(this.passeLivreService.getTickets('INACTIVE'));

    this.ticketsRequestStatus.loading = true;

    forkJoin([active$, cancelled$, inactive$]).subscribe({
      next: (values: TicketsResponse[]) => {
        this.mountCards(values);
        const activeTickets = values[0]?.tickets ?? [];
        this.loadActiveSeatCounts(activeTickets);
        this.loadActiveStandbyCounts(activeTickets);
      },
      error: () => {
        this.ticketsRequestStatus.error = true;
        this.notificationService.error('Ocorreu um erro ao buscar os tickets.');
      }
    }).add(() => {
      this.ticketsRequestStatus.loading = false;
      this.changeDetector.markForCheck();
    });
  }

  public mountCards(tickets: TicketsResponse[]): void {
    this.cards.clear();
    this.ticketMap.clear();
    const status = [StatusTicketCard.ACTIVE, StatusTicketCard.CANCELLED, StatusTicketCard.INACTIVE];

    // Recorre la lista de Tickets y agrupa por pnrCode
    tickets.forEach(ticketsResponse => {
      ticketsResponse.tickets.forEach(ticket => {
        if (this.ticketMap.has(ticket.pnrCode)) {
          this.ticketMap.get(ticket.pnrCode)?.push(ticket);
        } else {
          this.ticketMap.set(ticket.pnrCode, [ticket]);
        }
      });
    });

    // Crear un Set ORIGINAL para rastrear representaciones en cadena de los objetos
    this.ticketMap.forEach((tickets, pnrCode) => {
      const uniqueTickets = Array.from(
        new Map(tickets.map(ticket => [JSON.stringify(ticket), ticket])).values()
      );
      this.ticketMap.set(pnrCode, uniqueTickets);
    });

   status.forEach((status, index) => {
      if (tickets[index] && tickets[index].tickets.length > 0) {
        this.cards.set(status, this.orderTickets(tickets[index].tickets));
      }
    });
    
    //limpia mapas
    this.ticketMapActive.clear();
    this.ticketMapInactive.clear();
    this.ticketMapCancelled.clear();

    //Se recorre map cards para llenar los maps de tickets activos, inactivos y cancelados
    this.cards.forEach((value, key) => {
      //llena map activos
      if (key == 'ticket_active'){
        value.forEach(ticket => {
            if (this.ticketMapActive.has(ticket.pnrCode)) {
              this.ticketMapActive.get(ticket.pnrCode)?.push(ticket);
            } else {
              this.ticketMapActive.set(ticket.pnrCode, this.orderTickets([ticket]));
            }
          
        });
      }
      //llena map inactivos
      if (key == 'ticket_inactive'){
        value.forEach(ticket => {
            if (this.ticketMapInactive.has(ticket.pnrCode)) {
              this.ticketMapInactive.get(ticket.pnrCode)?.push(ticket);
            } else {
              this.ticketMapInactive.set(ticket.pnrCode, this.orderTickets([ticket]));
            }
          
        });
      }
      //llena map cancelados
      if (key == 'ticket_cancelled'){
        value.forEach(ticket => {
            if (this.ticketMapCancelled.has(ticket.pnrCode)) {
              this.ticketMapCancelled.get(ticket.pnrCode)?.push(ticket);
            } else {
              this.ticketMapCancelled.set(ticket.pnrCode, this.orderTickets([ticket]));
            }
        });
      }
    });

    // The lists read from the most recent flight to the oldest. The API returns the tickets
    // unordered and the maps above were filled oldest first (the legs of a PNR must stay
    // chronological, see orderTickets), so only the order of the PNR groups changes here.
    this.ticketMapActive = MyFlightsComponent.mostRecentFirst(this.ticketMapActive);
    this.ticketMapInactive = MyFlightsComponent.mostRecentFirst(this.ticketMapInactive);
    this.ticketMapCancelled = MyFlightsComponent.mostRecentFirst(this.ticketMapCancelled);
  }

  /**
   * Same groups, ordered by the departure of their first leg, latest first. A new Map is built: the
   * template iterates the maps with `keyvalue`, which keeps insertion order for string keys.
   */
  static mostRecentFirst(groups: Map<string, TicketResponse[]>): Map<string, TicketResponse[]> {
    const departureOf = (tickets: TicketResponse[]) => new Date(tickets[0]?.departureDateLt).valueOf() || 0;
    const ordered = Array.from(groups.entries())
      .sort(([, a], [, b]) => departureOf(b) - departureOf(a));
    return new Map(ordered);
  }

  private loadActiveSeatCounts(tickets: TicketResponse[]): void { 
    const segments = this.collectActiveSegments(tickets);
  
    if (segments.length === 0) {
      return;
    }
  
    this.passeLivreService
      .getSeatCounts(segments)
      .subscribe({
        next: (response: SeatCountResponse) => {
          this.mapSeatCountsToTickets(
            tickets,
            response.flights);
  
          this.changeDetector.markForCheck();
        },
        error: () => {
          for (const ticket of tickets) {
            ticket.availableSeats = null;
          }
  
          this.changeDetector.markForCheck();
        }
      });
  }
  
  private collectActiveSegments( tickets: TicketResponse[]): Array<{flightNumber: string; origin: string; departureDate: string;}> {
    const seen = new Set<string>();
    const result: Array<{flightNumber: string; origin: string; departureDate: string; }> = [];
  
    for (const ticket of tickets) {
      // undefined representa el estado cargando.
      ticket.availableSeats = undefined; 
      const departureDate = ticket.departureDateLt.substring(0, 10);
      const key = `${ticket.flightNumber}|` + `${ticket.departure}|` + `${departureDate}`;
  
      if (!seen.has(key)) {
        seen.add(key);
        result.push({flightNumber: ticket.flightNumber, origin: ticket.departure,departureDate });
      }
    }
    return result;
  }
  
  private mapSeatCountsToTickets(tickets: TicketResponse[],flights: SeatCountFlight[]): void {
    const countMap = new Map<string, number | null>();
  
    for (const flight of flights) {
      const key =  `${flight.flightNumber}|` + `${flight.origin}|` + `${flight.departureDate}`;
      countMap.set(key, flight.totalAvailableSeats);
    }
  
    for (const ticket of tickets) {
      const departureDate = ticket.departureDateLt.substring(0, 10); 
      const key =`${ticket.flightNumber}|` + `${ticket.departure}|` + `${departureDate}`;
      ticket.availableSeats = countMap.has(key) ? countMap.get(key)!: null;
    }
  }

  private loadActiveStandbyCounts(tickets: TicketResponse[]): void {
    type StandbyKey = { flightDate: string; flightNumber: string; departure: string };
    const seen = new Set<string>();
    const keys: StandbyKey[] = [];

    for (const ticket of tickets) {
      ticket.standbyCount = undefined;
      ticket.standbySummary = undefined;
      const flightDate = ticket.departureDateLt.substring(0, 10);
      const cacheKey = `${flightDate}|${ticket.flightNumber}|${ticket.departure}`;
      if (!seen.has(cacheKey)) {
        seen.add(cacheKey);
        keys.push({ flightDate, flightNumber: ticket.flightNumber, departure: ticket.departure });
      }
    }

    if (keys.length === 0) {
      return;
    }

    const requests = keys.map(k =>
      this.passeLivreService.getFlightCrew(k.flightDate, k.flightNumber, k.departure).pipe(
        timeout(15000),
        catchError(() => of(null))
      )
    );

    forkJoin(requests).subscribe({
      next: (results: Array<FlightCrewSummaryResponse | null>) => {
        const summaryMap = new Map<string, FlightCrewSummaryResponse | null>();
        keys.forEach((k, i) => {
          const cacheKey = `${k.flightDate}|${k.flightNumber}|${k.departure}`;
          summaryMap.set(cacheKey, results[i]);
        });

        for (const ticket of tickets) {
          const flightDate = ticket.departureDateLt.substring(0, 10);
          const cacheKey = `${flightDate}|${ticket.flightNumber}|${ticket.departure}`;
          const summary = summaryMap.get(cacheKey) ?? null;
          ticket.standbySummary = summary;
          ticket.standbyCount = summary
            ? (summary.totalLatam ?? 0) + (summary.totalExternal ?? 0)
            : null;
        }

        this.changeDetector.markForCheck();
      },
      error: () => {
        for (const ticket of tickets) {
          ticket.standbyCount = null;
          ticket.standbySummary = null;
        }
        this.changeDetector.markForCheck();
      }
    });
  }

  /** Legs in travel order (earliest departure first); returns a copy, the input is left as is. */
  public orderTickets(tickets: TicketResponse[]): TicketResponse[] {
    return [...tickets].sort((a: TicketResponse, b: TicketResponse) => {
      return new Date(a.departureDateLt).valueOf() - new Date(b.departureDateLt).valueOf();
    });
  }

  trackByTicketNumber(_index: number, ticketPair: KeyValue<string, TicketResponse[]>): string {
  return ticketPair.key;
  }


}
